import { useEffect, useRef, useState } from 'react';
import { reconcileBulkEmployee } from 'api/incidence.api';
import { ApiError } from 'types';
import {
  BulkReconciliationPayload,
  BulkReconciliationPreview,
  EmployeeReconciliationOutcome,
  ReconciliationEmployee,
} from 'types/incidences';

export type QueueEmployee = ReconciliationEmployee & {
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'unconfirmed';
  message?: string;
};
export type QueueStatus =
  | 'idle'
  | 'running'
  | 'pausing'
  | 'paused'
  | 'finished';

const resultMessages: Record<EmployeeReconciliationOutcome['code'], string> = {
  COMPLETED: 'Incidencias conciliadas.',
  NOT_ELIGIBLE: 'El empleado ya no es elegible para esta conciliación.',
  INVALID_CONTRACT_KIND:
    'El tipo de contrato ya no está disponible. Revisa los parámetros antes de iniciar otro proceso.',
  RECONCILIATION_FAILED:
    'No se pudo completar la conciliación. Puede haber cambios parciales.',
  SERVICE_UNAVAILABLE:
    'El servicio no está disponible. Puede haber cambios parciales.',
};

export const isCompletePreview = (
  preview: BulkReconciliationPreview
): boolean =>
  Boolean(
    preview &&
    typeof preview.contractKind?.id === 'string' &&
    typeof preview.contractKind.name === 'string' &&
    typeof preview.applicationDate === 'string'
  ) &&
  Number.isInteger(preview.employeeCount) &&
  preview.employeeCount >= 0 &&
  Array.isArray(preview.employees) &&
  preview.employees.length === preview.employeeCount &&
  preview.employees.every(
    (employee) =>
      employee &&
      typeof employee.employeeId === 'string' &&
      /^[a-f\d]{24}$/i.test(employee.employeeId) &&
      typeof employee.employeeNumber === 'string' &&
      typeof employee.employeeName === 'string'
  ) &&
  new Set(preview.employees.map((employee) => employee.employeeId)).size ===
    preview.employeeCount;

export const useReconciliationQueue = () => {
  const [employees, setEmployees] = useState<QueueEmployee[]>([]);
  const [status, setStatus] = useState<QueueStatus>('idle');
  const [notice, setNotice] = useState('');
  const employeesRef = useRef<QueueEmployee[]>([]);
  const payloadRef = useRef<BulkReconciliationPayload | null>(null);
  const runningRef = useRef(false);
  const pauseRef = useRef(false);
  const mountedRef = useRef(false);
  const generationRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
      pauseRef.current = true;
    };
  }, []);

  const updateEmployee = (id: string, update: Partial<QueueEmployee>) => {
    employeesRef.current = employeesRef.current.map((employee) =>
      employee.employeeId === id ? { ...employee, ...update } : employee
    );
    setEmployees(employeesRef.current);
  };

  const run = async () => {
    if (runningRef.current || !payloadRef.current || !mountedRef.current)
      return;
    runningRef.current = true;
    pauseRef.current = false;
    const generation = generationRef.current;
    const payload = payloadRef.current;
    setStatus('running');
    setNotice('');
    try {
      while (
        !pauseRef.current &&
        mountedRef.current &&
        generation === generationRef.current
      ) {
        const employee = employeesRef.current.find(
          (item) => item.status === 'pending'
        );
        if (!employee) break;
        updateEmployee(employee.employeeId, { status: 'processing' });
        try {
          const result = await reconcileBulkEmployee({
            ...payload,
            employee: employee.employeeId,
          });
          if (!mountedRef.current || generation !== generationRef.current)
            return;
          updateEmployee(employee.employeeId, {
            status: result.outcome,
            message: resultMessages[result.code],
          });
          if (result.pauseQueue) {
            pauseRef.current = true;
            setNotice(resultMessages[result.code]);
          }
        } catch (error) {
          if (!mountedRef.current || generation !== generationRef.current)
            return;
          const unauthorized =
            error instanceof ApiError && [401, 403].includes(error.statusCode);
          const message = unauthorized
            ? 'La solicitud fue rechazada porque tu sesión o tus permisos cambiaron.'
            : 'Resultado sin confirmar. El servidor podría seguir procesando a este empleado.';
          updateEmployee(employee.employeeId, {
            status: unauthorized ? 'failed' : 'unconfirmed',
            message,
          });
          pauseRef.current = true;
          setNotice(
            unauthorized
              ? `${message} El proceso está pausado.`
              : 'Se perdió la confirmación del servidor. Continuar pendientes no volverá a enviar al empleado sin confirmar; su resultado requiere verificación.'
          );
        }
      }
    } finally {
      if (mountedRef.current && generation === generationRef.current) {
        runningRef.current = false;
        setStatus(
          employeesRef.current.some((employee) => employee.status === 'pending')
            ? 'paused'
            : 'finished'
        );
      }
    }
  };

  const start = (
    preview: BulkReconciliationPreview,
    payload: BulkReconciliationPayload
  ) => {
    // Refs prevent double starts even before React commits the disabled button.
    if (
      payloadRef.current ||
      !isCompletePreview(preview) ||
      !preview.employeeCount
    )
      return;
    payloadRef.current = { ...payload };
    employeesRef.current = preview.employees.map((employee) => ({
      ...employee,
      status: 'pending',
    }));
    setEmployees(employeesRef.current);
    void run();
  };
  const pause = () => {
    if (!runningRef.current) return;
    pauseRef.current = true;
    setStatus('pausing');
  };
  const abandon = () => {
    // Do not abort the current write; only prevent further requests, including
    // when navigation reuses this route instead of unmounting immediately.
    generationRef.current += 1;
    pauseRef.current = true;
    runningRef.current = false;
  };
  const counts = {
    completed: employees.filter((employee) => employee.status === 'completed')
      .length,
    failed: employees.filter((employee) => employee.status === 'failed').length,
    pending: employees.filter((employee) => employee.status === 'pending')
      .length,
    unconfirmed: employees.filter(
      (employee) => employee.status === 'unconfirmed'
    ).length,
    processing: employees.filter((employee) => employee.status === 'processing')
      .length,
  };
  return {
    employees,
    status,
    notice,
    counts,
    start,
    pause,
    resume: run,
    abandon,
    shouldWarn: counts.pending + counts.processing + counts.unconfirmed > 0,
  };
};
